import { IAsset } from '../constants/assets';
import { BookLabelName } from './exemplars';

export enum TextTypes {
  recount = 'Recount',
  description = 'Description',
  informationReport = 'Information report',
  narrative = 'Narrative',
  procedure = 'Procedure',
  exposition = 'Exposition',
  explanation = 'Explanation',
  discussion = 'Discussion',
  hybrid = 'Hybrid',
}

export type TextType = {
  exemplarId: number;
  title: string;
  additionalTextTypes?: string[];
};

export type LoginRequest = {
  email: string;
  accessCode: string;
  agreement: boolean;
};

export type AccessCodeRequest = {
  accessCode: string;
  agreement: boolean;
};

export type UserInfo = {
  email: string;
};

export type RedeemedCollection = {
  collection: string;
  stage: string;
  defaultStage: string;
  access: string;
  trialDaysLeft: string;
  errors: { accessCode: string };
};

export type SuccessfulResponse = {
  message: string;
};

export type Token = {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
};

export type TokenDecoded = {
  iss: string;
  iat: number;
  exp: number;
  nbf: number;
  jti: string;
  sub: number;
  prv: string;
  email: string;
  defaultStage: string;
  access: string;
  trialDaysLeft: number;
};

export type ExemplarType = {
  id: number;
  title: string;
  image: string;
  textTypes: TextType[];
  engagement: IAsset;
};

export type LanguageFeatureLabel = {
  label: string;
  color: string;
};

export type Word = {
  text?: string;
  language?: string;
  style?: Array<string>;
  id?: number;
  highlighted?: boolean;
  color?: string;
  font?: { bold: boolean; italic: boolean } | undefined;
  isHidden?: boolean;
  lineIndex?: number;
};

export type ExemplarWord = {
  text: string;
  id: string;
  wordId: string | number;
  lineId: string | number;
  sectionId: string;
  wordIndex: number;
  lineIndex: number;
  sectionIndex: number;
  side: string;
  style?: string[];
  color?: string;
  hasDefaultLanguage?: boolean;
  language?: string;
  type?: string;
  label?: string;
  highlight?: string;
  prepositional_phrase?: boolean;
  phrase?: boolean;
  text_type?: string;
};

interface LanguageFeatures {
  language: string;
  isPhrase: boolean;
  isPrepositionalPhrase: boolean;
}

interface TextPresentation {
  text_type: string;
}

interface LanguageFeatureSpace extends Partial<LanguageFeatures> {
  type: 'space';
}

export interface SimpleLanguageFeatureWord extends Partial<LanguageFeatures> {
  type: 'word';
  word: ExemplarWord;
}

export type WordWithLanguageFeatures = LanguageFeatureSpace | SimpleLanguageFeatureWord;

interface TextPresentationSpace extends Partial<TextPresentation> {
  type: 'space';
}

interface SimpleTextPresentationWord extends Partial<TextPresentation> {
  type: 'word';
  word: ExemplarWord;
}

export type WordWithTextPresentation = TextPresentationSpace | SimpleTextPresentationWord;

export type ContentLine = {
  id?: string;
  words: Array<ExemplarWord>;
};

export type ProcedureContentLine = {
  id?: string;
  words: Array<ExemplarWord>;
  style?: Array<string>;
  sequence_number?: string;
};

export type ProformaLine = {
  id?: string;
  words: Word[];
};

export type Section = {
  id: string;
  type: 'section';
  lines: Array<ContentLine | ProcedureContentLine>;
  label?: string;
  description?: string;
  color?: string;
  style?: Array<string>;
};

export type AWSImage = {
  url: string;
  uuid: string;
};

export type Image = {
  type: 'image';
  editable: boolean;
  uuid: string;
  default_uuid?: string;
  defaultImageDimensions?: ImageDimensions;
  isPlaceholder?: boolean;
  key?: string;
  id: string;
  position?: ImagePosition;
};

export type ImagePosition = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type EditableSection =
  | (Section & {
      defaultImageDimensions?: ImageDimensions;
      isPlaceholder?: boolean;
    })
  | Image;

export type Side = {
  left: Array<Section | Image>;
  right: Array<Section | Image>;
  bottom: Array<Image>;
};

export type ContentWithBottom = {
  left: Section[];
  right: Section[];
  bottom: Image[];
};

export type LaterExemplar = {
  meta: {
    isIntroducedToHybrid?: boolean;
    highlightedLanguageFeatureLabels: Record<string, boolean>;
    highlightedSectionLabels: Record<string, boolean>;
  };
  is_default?: boolean;
  title: string;
  guid: string;
  stage: string;
  text_type: string;
  additional_text_types?: string[];
  teacher_focus: Array<TeacherFocus>;
  language_feature_labels: LanguageFeatureLabel[];
  section_labels: SectionLabel[];
  content: Side;
  collection: string;
  relationships: ExemplarRelationships;
  page?: string;
  tools?: EditedExemplarTools;
};

export type ImageSection = {
  type?: string;
  url?: string;
  imageUUIDtoReplace: string;
  uuid: string;
  default_uuid?: string;
  sectionId: string;
  side: ImageSide;
  selectedImageDimensions: ImageDimensions;
  defaultImageDimensions?: ImageDimensions;
};

export type RelatedTexts = {
  default: Array<RelatedObject>;
  saved: Array<RelatedObject>;
};

export enum RelatedType {
  exemplars = 'exemplars',
  proformas = 'proformas',
  saved_proformas = 'saved_proformas',
  saved_exemplars = 'saved_exemplars',
}

export enum WritingType {
  exemplars = 'exemplars',
  proformas = 'proformas',
}

export type RelatedObject = {
  id: number;
  textType: string;
  title: string;
  thumbnail: string;
  stage: string;
  type: RelatedType;
  page?: string;
};

export type SectionLabel = {
  label: string;
  description: string;
  color: string;
};

export type Highlighted = Record<string, boolean>;

export type SectionColor = {
  title: string;
  text: string;
};

export type SectionsColors = Record<string, SectionColor>;

export type TeacherFocus = {
  screen: string;
  content: string;
};

export type EmergentContentSection = {
  type: 'section';
  style?: Array<string>;
  lines: Array<ContentLine>;
  color?: string;
  id: string | number;
  uuid: string;
  default_uuid: string | undefined;
  key: string;
};

export enum ImageSide {
  left = 'left',
  right = 'right',
  bottom = 'bottom',
}

export interface ImageDimensions {
  width: number;
  height: number;
  exemplarWidth: number;
  exemplarHeight: number;
}

export enum VisibilityState {
  visible = 'visible',
  hidden = 'hidden',
}

export type WordsAndSentencesContent = {
  type: 'screen';
  label: string;
  content: {
    left: Array<EmergentContentSection | Image>;
    right: Array<EmergentContentSection | Image>;
    bottom: Array<EmergentContentSection | Image>;
  };
};

export interface IImage {
  url: string;
  position: ImagePosition;
  id?: number;
  isHidden?: boolean;
  editable: boolean;
  default_uuid?: string;
  defaultPosition: ImagePosition;
  uuid: string;
  key: string;
}

export interface SelectedImage {
  id: string;
  sectionId: string;
  side?: ImageSide;
  dimensions?: ImageDimensions;
  default_uuid?: string;
}

export interface SelectedWordsImage {
  id: string;
  sectionId: string;
  default_uuid?: string;
  dimensions?: ImageDimensions;
}

export interface SelectedEmergentImage extends SelectedWordsImage {
  side?: ImageSide;
}

export interface ISelectionRange {
  selectionStart: number;
  width: string;
}

export interface IPosition {
  y: number;
  x: number;
}

export interface ILabel {
  id: number;
  text: string;
  selectionRange: ISelectionRange;
  position: IPosition;
  isHidden: boolean;
  isFocussed: boolean;
  originalIsManuallyHidden?: boolean;
  isManuallyHidden: boolean;
  isHighlighted: boolean;
}

export type WordsPageTitle = {
  lines: ContentLine[];
  color?: string;
};

export type WordsPageContent = {
  type: 'screen';
  label: string;
  images?: Array<IImage>;
  words?: Array<ILabel>;
  title?: WordsPageTitle;
  label_color?: string;
};

export type EmergentExemplar = {
  is_default?: boolean;
  json_version: string;
  title: string;
  stage: string;
  text_type: string;
  additional_text_types?: string[];
  collection: string;
  teacher_focus: Array<TeacherFocus>;
  content: Array<WordsAndSentencesContent | WordsPageContent>;
  relationships: ExemplarRelationships;
  page?: string;
  tools?: EditedExemplarTools;
};

export type ExemplarCombined = EmergentExemplar | LaterExemplar | Proforma | undefined;

export type IWordsImageParams = {
  imageUUIDtoReplace: string;
  uuid: string;
};

export type IWordsAndSentencesImageParams = {
  sectionId?: string | number;
  imageUUIDtoReplace: string;
  uuid: string;
  default_uuid: string | undefined;
  defaultImageDimensions: ImageDimensions;
  selectedImageDimensions: ImageDimensions;
  key?: string;
  mode?: string;
  side: ImageSide;
};

export type IDefaultImages = {
  defaultImages: AWSImage[];
};

export interface INote {
  id?: string;
  selectionStart: number;
  isManuallyHidden: boolean;
  isFocussed: boolean;
  content: string;
  position: IPosition;
}

export type LineType = {
  tool: string;
  points: Array<number>;
  drawingColor: string;
};

export type ProformaSectionLabel = {
  label: string;
  description: string;
  help_text: string;
};

export type ProformaLanguageLabel = {
  label: string;
  words: Array<string>;
};

export type ProformaSideSection = {
  id?: string;
  type: string;
  label?: string;
  lines: Array<ProformaLine>;
  style?: Array<string>;
};

export type ProformaImageSection = {
  id?: string;
  editable?: boolean;
  isPlaceholder?: boolean;
  type: 'image';
  uuid?: string;
  url?: string;
};

export type ProformaContent = {
  [side: string]: Array<ProformaSideSection | ProformaImageSection>;
};

export type Proforma = {
  id: number;
  title: string;
  stage: string;
  text_type: string;
  section_labels: Array<ProformaSectionLabel>;
  language_feature_labels: Array<ProformaLanguageLabel>;
  content: ProformaContent;
  page?: string;
  relationships: ExemplarRelationships;
};

export type EditedWord = {
  text: string;
  style?: string[];
  language?: string;
  highlight?: string;
};

export type EditedSectionLine = {
  words: EditedWord[];
  style?: string[];
  sequence_number?: string;
};

export type EditedSideSection = {
  type: 'section';
  label: string;
  style?: string[];
  lines: EditedSectionLine[];
};

export type EditedImage = {
  type: 'image';
  url: string;
  uuid: string;
  position?: ImagePosition;
};

export type EditedExemplarContent = {
  left: Array<EditedSideSection | EditedImage>;
  right: Array<EditedSideSection | EditedImage>;
  bottom?: Array<EditedImage>;
};

export type EditedWordsAndSentencesContent = {
  type: string;
  label: string;
  content: EditedExemplarContent;
};

export type EditedWordsPageTitle = {
  lines: {
    words: EditedWord[];
  }[];
};

export type EditedWordsContent = {
  type: string;
  label: string;
  images?: Array<IImage>;
  words?: Array<SavedLabel>;
  title: EditedWordsPageTitle;
  label_color?: string;
};

export type EditedEmergentExemplarContent = Array<EditedWordsContent | EditedWordsAndSentencesContent>;

export type SavedPenLine = {
  color: string;
  points: number[];
};

type SavedPen = {
  lines: SavedPenLine[];
  isHidden: boolean;
};

export type SavedNote = {
  content: string;
  position: IPosition;
  isHidden: boolean;
};

export type SavedLabel = {
  text: string;
  position?: IPosition;
  isHidden?: boolean;
  isManuallyHidden?: boolean;
};

export type SavedHighlights = {
  isHidden: boolean;
};

export type EditedExemplarTools = {
  pen?: SavedPen;
  notes?: SavedNote[];
  isTextHidden?: boolean;
  highlights?: SavedHighlights;
};

export interface EditedExemplar {
  id?: number;
  title: string;
  page: string;
  content: EditedExemplarContent | EditedEmergentExemplarContent;
  tools: EditedExemplarTools;
}

export type EditedSavedExemplar = Omit<EditedExemplar, 'title' | 'page'>;

export type Exemplar = EmergentExemplar | LaterExemplar;

export interface StagesData {
  stageId: number;
  title: string;
  items: Collection[];
}

export interface Collection {
  collectionId: string;
  title: BookLabelName;
}

export interface CollectionData {
  exemplarId: number;
  textTypeName: string;
  textTypeId: number;
  collectionName: BookLabelName;
  stageName: string;
}

export interface ExemplarDataLesson {
  exemplarId: number;
  textType: string;
  title: string;
  stageId: string;
  stage: string;
  collectionId: string;
  collectionName: string;
  textTypeId: string;
  textTypeName: string;
  thumbnail: string;
  lessonId: string;
}

export type ExemplarRelationships = {
  images: Array<AWSImage>;
};

export type DropdownOption = {
  name: string;
  callback: () => void;
};

export type SeparatedStyledWord = {
  text: string;
  style?: string[];
};

export type FilteredLaterTextSection = {
  lines: {
    words: {
      text: string;
      style?: string[];
    }[];
  }[];
};

export type FilteredEmergentTextSection = {
  lines: {
    words: {
      text: string;
      style?: string[];
    }[];
  }[];
  style?: string[];
};

export type FilteredProcedureSection = {
  lines: {
    words: {
      text: string;
      style?: string[];
    }[];
    style?: string[];
  }[];
  style?: string[];
};

export interface ReplacedImage {
  imageUUIDtoReplace: string;
  uuid: string;
  default_uuid: string | undefined;
  defaultImageDimensions: ImageDimensions;
  selectedImageDimensions: ImageDimensions;
  sectionId: string;
  side?: ImageSide;
}
